import { Endpoint } from "shared/utils/endpoints";
import { SCHOOL_HTTP_CLIENT } from "shared/utils/schoolInterceptor";
import { scoped } from "./schoolScope";

/**
 * Single-school (campus) analytics APIs, called from the admin panel — the
 * campus-scoped twin of `headOfficeService`.
 *
 * A school whose `is_multi_campus` is 0 has no campuses, so it reports classes
 * instead: these `school/*-v1` endpoints return `data.classes[]` where the
 * head-office ones return campuses. Every call is scoped to the school the
 * screen registered through `setSchoolScopeId`.
 *
 * Common params: `from`/`to` (YYYY-MM-DD window), `per_page` (1-50) + `page`.
 */

const CA = Endpoint.school_campus;

interface DateRangeParams {
  from?: string;
  to?: string;
}

interface PaginatedParams extends DateRangeParams {
  per_page?: number;
  page?: number;
}

/** Grade drill-down lens: which slice of the class to return. */
interface GradeDrilldownParams extends PaginatedParams {
  /** student (default) | class-health | teacher */
  type?: "student" | "class-health" | "teacher";
}

/** KPI tiles: enrolled_teachers, enrolled_students, classrooms, books, pages. */
const getDashboard = (params?: DateRangeParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.dashboard, { params: scoped(params) });

/** Per-class reading summary, sorted by total_books_read desc (paginated). */
const getReaderClass = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.readerClass, { params: scoped(params) });

/** One class's windowed reading detail. `classId` is the classroom id. */
const getReaderClassDetail = (
  classId: number | string,
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(CA.readerClassDetail + classId, {
    params: scoped(params),
  });

/**
 * Class drill-down through a single lens.
 * `type=student|class-health|teacher`; `classId` is the classroom id.
 */
const getGradeDrilldown = (
  classId: number | string,
  params?: GradeDrilldownParams
) =>
  SCHOOL_HTTP_CLIENT.get(CA.gradeDrilldown + classId, {
    params: scoped(params),
  });

/** One student's windowed reading detail. */
const getStudentDetail = (
  studentId: number | string,
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(CA.studentDetail + studentId, {
    params: scoped(params),
  });

/** One teacher's windowed detail. */
const getTeacherDetail = (
  teacherId: number | string,
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(CA.teacherDetail + teacherId, {
    params: scoped(params),
  });

/** Whole-school adoption rate + age-appropriate share. */
const getAdoptionRate = (params?: DateRangeParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.adoptionRate, { params: scoped(params) });

/**
 * Share-of-reads per dimension.
 * @param types one or more of genre|language|age_range|keyword (comma-separated).
 */
const getReadingMix = (types: string = "genre", params?: DateRangeParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.readingMix, { params: scoped({ types, ...params }) });

/** Teachers ranked by completed then assigned books (paginated). */
const getTopTeachers = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.topTeachers, { params: scoped(params) });

/** Students ranked by completed books then badges (paginated). */
const getTopStudents = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.topStudents, { params: scoped(params) });

/** Headline content stats + per-class completed-book counts (paginated). */
const getBookContentAnalysis = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.bookContentAnalysis, { params: scoped(params) });

/** Per-teacher engagement (quiz pass, classes) — paginated. */
const getTeacherAdoption = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.teacherAdoption, { params: scoped(params) });

/**
 * Reading-growth line-graph series over the window (window <= 365 days).
 * @param metrics one or more of books_read|pages_read|cumulative_reading|
 *                learning_rate|book_completion_rate|avg_books_per_active_student.
 */
const getReadingGrowthTrend = (
  metrics: string = "books_read",
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(CA.readingGrowthTrend, {
    params: scoped({ metrics, ...params }),
  });

/** Class-by-class comparison on adoption / quiz / completion / age (paginated). */
const getClassComparison = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(CA.classComparison, { params: scoped(params) });

export {
  getDashboard,
  getReaderClass,
  getReaderClassDetail,
  getGradeDrilldown,
  getStudentDetail,
  getTeacherDetail,
  getAdoptionRate,
  getReadingMix,
  getTopTeachers,
  getTopStudents,
  getBookContentAnalysis,
  getTeacherAdoption,
  getReadingGrowthTrend,
  getClassComparison,
};
